/**
 * hrisRoleModel — the role catalogue and how a role resolves to a grant.
 *
 * WHY IT IS ITS OWN FILE. `hrisRoles.ts` keeps the config in a SharePoint
 * store, which pulls in the SP client, which constructs MSAL at import — so a
 * unit test that touches any of it dies on a missing tenant id before the
 * first assertion. Access control is precisely the thing that should be
 * testable, so the rules live here, as pure functions over a config that is
 * handed in rather than fetched. Same split as hrisStyleVars.ts and
 * payrollStepState.ts.
 *
 * `hrisRoles` re-exports all of it, so no call site knows.
 */
import { HRIS_SCREENS, type HrisScreenKey } from "./hrisStyleVars";

export const ALL_KEYS: HrisScreenKey[] = HRIS_SCREENS.map((s) => s.key);
const VALID = new Set<string>(ALL_KEYS);

export interface IHrisRole {
  key: string;
  name: string;
  description: string;
  /** Grants every screen plus Settings, Discovery, 201 and the firm exports. */
  full: boolean;
  /** Screens this role may open (ignored when `full`). */
  screens: HrisScreenKey[];
  /**
   * May APPROVE or REJECT a leave request, not merely read the list.
   *
   * Its own capability rather than a by-product of `full`, because deciding
   * somebody's leave and seeing their net pay are different privileges and the
   * people who do the first are usually not the people trusted with the
   * second. Leaving them conflated is what made the Supervisor role below
   * describe itself as "Approves leave" while being unable to approve
   * anything, and what left Operations reading requests it could not action.
   *
   * Opening the Leaves screen is still required — `hrisAccessFor` ANDs the two,
   * so this can never grant a decision to somebody who cannot see the request.
   */
  mayDecideLeave: boolean;
  builtIn: boolean;
}

export interface IHrisUserAssignment {
  /** Lowercase email — the identity MSAL reports. */
  email: string;
  /** Display name, for the roster only. */
  name: string;
  roleKey: string;
  /** Extra screens on top of the role, for one-off exceptions. */
  extraScreens: HrisScreenKey[];
  active: boolean;
  note: string;
}

export interface IHrisRoleConfig {
  roles: IHrisRole[];
  users: IHrisUserAssignment[];
}

/** The roles the company actually runs, mirroring how the module is used. */
export const BUILT_IN_ROLES: IHrisRole[] = [
  {
    key: "admin", name: "HRIS Administrator", full: true, screens: [...ALL_KEYS], mayDecideLeave: true, builtIn: true,
    description: "Everything, including Settings, Discovery, the 201 file and the accounting-firm exports.",
  },
  {
    key: "hr", name: "HR Officer", full: false, mayDecideLeave: true, builtIn: true,
    screens: ["dashboard", "employees", "attendance", "leaves", "loans"],
    description: "People management — employees, attendance, leaves and loans. No pay figures.",
  },
  {
    key: "payroll", name: "Payroll Officer", full: false, mayDecideLeave: false, builtIn: true,
    screens: ["dashboard", "payroll", "periods", "payslips", "reports", "attendance"],
    description: "Runs the payroll cycle end to end, plus the attendance it is computed from.",
  },
  {
    key: "supervisor", name: "Supervisor", full: false, mayDecideLeave: true, builtIn: true,
    screens: ["leaves", "attendance"],
    description: "Approves leave and reviews attendance for their people. No payroll access.",
  },
  {
    key: "viewer", name: "Dashboard Viewer", full: false, mayDecideLeave: false, builtIn: true,
    screens: ["dashboard"],
    description: "The HRIS dashboard only — headcount and trends, no records.",
  },
  {
    key: "none", name: "No Access", full: false, screens: [], mayDecideLeave: false, builtIn: true,
    description: "Keeps the person on the roster with HRIS closed to them.",
  },
];

/** Only screen keys the app actually has — a stale saved key can't grant anything. */
const cleanScreens = (v: unknown): HrisScreenKey[] =>
  Array.isArray(v) ? (v.filter((k) => typeof k === "string" && VALID.has(k)) as HrisScreenKey[]) : [];

export function normalise(raw: Partial<IHrisRoleConfig> | null): IHrisRoleConfig | null {
  if (!raw) return null;
  const byKey = new Map<string, IHrisRole>();
  for (const b of BUILT_IN_ROLES) byKey.set(b.key, b);
  for (const r of raw.roles ?? []) {
    if (!r?.key) continue;
    const base = byKey.get(r.key);
    byKey.set(r.key, {
      key: r.key,
      name: r.name || base?.name || r.key,
      description: r.description ?? base?.description ?? "",
      // `full` on a built-in is fixed — only the admin role may be full.
      full: base ? base.full : false,
      screens: base?.full ? [...ALL_KEYS] : cleanScreens(r.screens),
      // Same treatment, and for a second reason: configs saved before this
      // field existed carry `undefined` for it, so reading the stored value
      // would silently strip approval from HR and Supervisor the first time
      // anybody opened Roles & Users. A built-in's answer comes from the
      // built-in; only a custom role is taken at its word.
      mayDecideLeave: base ? base.mayDecideLeave : r.mayDecideLeave === true,
      builtIn: !!base,
    });
  }
  const users: IHrisUserAssignment[] = [];
  for (const u of raw.users ?? []) {
    const email = (u?.email ?? "").trim().toLowerCase();
    if (!email) continue;
    users.push({
      email,
      name: u.name ?? "",
      roleKey: byKey.has(u.roleKey) ? u.roleKey : "none",
      extraScreens: cleanScreens(u.extraScreens),
      active: u.active !== false,
      note: u.note ?? "",
    });
  }
  return { roles: [...byKey.values()], users };
}

export function newUserAssignment(): IHrisUserAssignment {
  return { email: "", name: "", roleKey: "viewer", extraScreens: [], active: true, note: "" };
}

export const findRole = (config: IHrisRoleConfig, key: string): IHrisRole | undefined =>
  config.roles.find((r) => r.key === key);

/* ── Resolution ───────────────────────────────────────────────────────────── */

export interface IResolvedGrant {
  full: boolean;
  screens: Set<HrisScreenKey>;
  /** May decide a leave request — see IHrisRole.mayDecideLeave. */
  mayDecideLeave: boolean;
  /** The role that produced this, for display. Null when unassigned. */
  role: IHrisRole | null;
}

/**
 * What `config` grants this email, or null when the email has no active
 * assignment — the caller then falls back to the hard-coded lists.
 */
export function grantFromConfig(
  config: IHrisRoleConfig | null,
  email: string | undefined
): IResolvedGrant | null {
  if (!config) return null;
  const e = (email ?? "").trim().toLowerCase();
  if (!e) return null;
  const assignment = config.users.find((u) => u.email === e && u.active);
  if (!assignment) return null;
  const role = findRole(config, assignment.roleKey) ?? null;
  if (role?.full) return { full: true, screens: new Set(ALL_KEYS), mayDecideLeave: true, role };
  return {
    full: false,
    screens: new Set<HrisScreenKey>([...(role?.screens ?? []), ...assignment.extraScreens]),
    mayDecideLeave: role?.mayDecideLeave === true,
    role,
  };
}

export { ALL_KEYS as HRIS_ALL_SCREEN_KEYS };
